import { Edit, FolderClosed } from 'lucide-react'
import type { Project, Task } from '@/types'
import { projectRollup, projectPathLabel } from '@trackmind/core'
import { Button } from '@/components/ui/button'
import { projectStatusMeta } from '@/lib/project-status'
import { cn } from '@/lib/utils'

export type ProjectRowVariant = 'active' | 'paused' | 'completed' | 'archived'

/**
 * One project as a single ~52px row, for phones only.
 *
 * The desktop card spends ~150px on a title, a status badge, a description, a
 * "Progress 43%" line, a bar, a task count and hours remaining. Three columns
 * of those read well on a monitor; stacked on a 390px phone, a dozen projects
 * is half a dozen screens.
 *
 * The row keeps the name, the completion (as a number *and* a hairline bar,
 * because a bar alone can't be read precisely and a number alone can't be
 * scanned), and status as an icon. Description and hours remaining are inside
 * the project.
 *
 * The list is flat — every project at every depth gets a row — so a nested one
 * carries its parent trail inline ahead of its name. Without it two rows
 * called "Experiments" under different parents were indistinguishable, and a
 * folder whose work lives in its children read as "empty".
 */
export function ProjectRow({
  project,
  projects,
  tasks,
  variant = 'active',
  onOpen,
  onEdit,
}: {
  project: Project
  /** All projects, for the parent trail and the sub-project roll-up. */
  projects: Project[]
  /** All tasks; filtered here so callers don't each re-derive the same thing. */
  tasks: Task[]
  variant?: ProjectRowVariant
  onOpen: () => void
  onEdit?: () => void
}) {
  // Counts cover the whole subtree: partial credit for countable tasks, and
  // habits and archived work left out, same as everywhere else.
  const rollup = projectRollup(projects, tasks, project.id)
  const { total, completed, progress: percent } = rollup
  const ancestry = projectPathLabel(projects, project.id)

  // The variant the caller groups by, falling back to the project's own
  // status. `completed` used to be missing from this type entirely, so a
  // finished project drew the plain amber folder — identical to a live one.
  const status = projectStatusMeta(variant ?? project.status)
  const StatusIcon = status.Icon

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border bg-card px-3 py-2 active:bg-accent/50',
        variant !== 'active' && 'opacity-75',
      )}
      onClick={onOpen}
      style={project.color ? { borderLeftColor: project.color, borderLeftWidth: '4px' } : undefined}
    >
      {StatusIcon ? (
        <StatusIcon
          className={cn('h-4 w-4 shrink-0', status.iconClass)}
          aria-label={status.label}
        />
      ) : (
        <FolderClosed className="h-4 w-4 shrink-0 text-amber-500" />
      )}

      <div className="min-w-0 flex-1">
        {/* Trail inline rather than on a second line — a nested project still
            has to fit the one-row budget. */}
        <div className="truncate text-sm" title={ancestry ? `${ancestry} › ${project.name}` : project.name}>
          {ancestry && (
            <span className="text-muted-foreground">{ancestry} › </span>
          )}
          <span className={cn('font-medium', status.strike && 'line-through text-muted-foreground')}>
            {project.name}
          </span>
        </div>
        {total > 0 && (
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${Math.round(percent)}%` }}
            />
          </div>
        )}
      </div>

      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {total > 0 ? `${completed}/${total}` : 'empty'}
      </span>

      {onEdit && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
          aria-label={`Edit ${project.name}`}
        >
          <Edit className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
